import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Zap, Scissors, Star, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { heroData, servicesData, testimonialsData } from '../data/mockData';

const iconMap = {
  Sparkles,
  Zap,
  Scissors
};

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const featuredServices = servicesData.filter(service => service.featured);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonialsData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonialsData.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length);
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${heroData.backgroundImage})`,
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="fade-in mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            {heroData.title}
          </h1>
          <p className="text-xl md:text-2xl mb-8 fade-in-delay-1 font-light max-w-2xl mx-auto">
            {heroData.subtitle}
          </p>
          <Link to="/contact" className="btn-primary fade-in-delay-2">
            <span>{heroData.ctaText}</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section" style={{ background: 'var(--warm-gradient)' }}>
        <div className="container text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="mb-6 fade-in" style={{ color: 'var(--charcoal)' }}>
              Your Sanctuary of Beauty
            </h2>
            <p className="text-lg leading-relaxed fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
              Step into a world where luxury meets expertise. Our sanctuary is designed for those who 
              appreciate the finest in beauty treatments, skincare, and wellness. Every detail is crafted 
              to provide you with an unforgettable experience that leaves you feeling radiant and renewed.
            </p>
          </div>
        </div>
      </section>

      {/* Signature Services */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="mb-4 fade-in" style={{ color: 'var(--charcoal)' }}>
              Signature Services
            </h2>
            <p className="text-lg max-w-2xl mx-auto fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
              Discover our most coveted treatments, designed to enhance your natural beauty
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredServices.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div 
                  key={service.id} 
                  className={`group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 fade-in-delay-${index + 1}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 p-3 rounded-full" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="mb-3" style={{ color: 'var(--charcoal)' }}>
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold" style={{ color: 'var(--champagne-gold)' }}>
                        {service.price}
                      </span>
                      <Link 
                        to="/services" 
                        className="text-sm font-medium hover:underline flex items-center space-x-1"
                        style={{ color: 'var(--champagne-gold)' }}
                      >
                        <span>Learn More</span>
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section" style={{ background: 'var(--blush-gradient)' }}>
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="mb-4 fade-in" style={{ color: 'var(--charcoal)' }}>
              What Our Clients Say
            </h2>
            <p className="text-lg max-w-2xl mx-auto fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
              Hear from those who have experienced our transformative treatments
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
                <div className="flex-shrink-0">
                  <img 
                    src={testimonialsData[currentTestimonial].image}
                    alt={testimonialsData[currentTestimonial].name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex justify-center md:justify-start mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" style={{ color: 'var(--champagne-gold)' }} />
                    ))}
                  </div>
                  <p className="text-lg italic mb-4" style={{ color: 'var(--charcoal)' }}>
                    "{testimonialsData[currentTestimonial].text}"
                  </p>
                  <div className="font-semibold" style={{ color: 'var(--champagne-gold)' }}>
                    — {testimonialsData[currentTestimonial].name}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-white shadow-lg hover:shadow-xl transition-all"
              style={{ color: 'var(--champagne-gold)' }}
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-white shadow-lg hover:shadow-xl transition-all"
              style={{ color: 'var(--champagne-gold)' }}
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonialsData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentTestimonial ? 'scale-125' : 'opacity-50'
                  }`}
                  style={{ backgroundColor: 'var(--champagne-gold)' }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section bg-charcoal text-white" style={{ backgroundColor: 'var(--charcoal)' }}>
        <div className="container text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="mb-6 text-white fade-in">
              Discover Your Best Self Today
            </h2>
            <p className="text-xl text-gray-300 mb-8 fade-in-delay-1">
              Ready to experience the pinnacle of beauty and wellness? 
              Book your appointment and begin your transformation journey.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 fade-in-delay-2">
              <Link to="/contact" className="btn-primary">
                Book Your Appointment
              </Link>
              <Link to="/services" className="btn-secondary">
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;